/*!
 * pf-engine.js — Product Finder matching engine
 * ─────────────────────────────────────────────────────────────────────────
 * Scores the entire live L1–L6 category tree (theme/assets/pf-category-index.json,
 * ~380 unique collections / 523 nodes) against a buyer's Step 1 + Step 2 + Step 3
 * selections from sections/product-finder.liquid, and returns a ranked list of
 * matching collections — a primary best match plus optional close alternates.
 *
 * WHY THIS EXISTS
 * The old finder hardcoded exactly one collection URL per admin option slot
 * (s2_url_0..s2_url_4). Any hierarchy change silently broke it (35 of those
 * URLs pointed at collections that no longer exist). This engine instead
 * treats the admin's option labels as a *search query* against the real,
 * always-current hierarchy, so it keeps working after re-organizations and
 * can suggest multiple relevant categories instead of a single brittle link.
 *
 * ALGORITHM (TF-IDF style relevance, same family of math as real search
 * engines, scoped to ~500 short "documents" so it's instant client-side):
 *   1. Tokenize the combined Step 1 + Step 2 + Step 3 option text the buyer
 *      selected (label + sub_label for each).
 *   2. For every node in the tree, score += for each query token that is
 *      also one of the node's own keywords:
 *        - 3x weight if the token is a *title* keyword (own title + direct
 *          parent title) — this is the strongest, most precise signal.
 *        - 1x weight if the token only appears in that node's *product*
 *          keywords (derived from real product titles under it) — catches
 *          synonyms/materials/use-cases a category name wouldn't contain.
 *      Each hit is further multiplied by that token's global IDF weight
 *      (rarer, more distinctive words count far more than generic ones
 *      like "lab" or "safety" that appear on hundreds of nodes).
 *   3. Exact-title bonus: tokens from the Step 1 option's own *label* that
 *      exactly match a node's own title give a strong flat bonus, so an
 *      option literally called "Laboratory Safety" reliably wins the real
 *      "Laboratory Safety" category over a deep leaf that merely shares a
 *      couple of generic sub-label words.
 *   4. Inventory-depth tie-breaker: log(1 + totalProducts) nudges a branch
 *      with real stock behind it ahead of a near-empty sibling.
 *   5. Results are grouped so we never recommend the same handle twice and
 *      never recommend a node that is an ancestor of a higher-scoring node
 *      already in the result set (no "Laboratory Safety" AND its own child
 *      both cluttering the result — the more specific one wins).
 *
 * The admin is still 100% in control from the Theme Customizer: every
 * Step 1/2/3 block's label/sub_label *is* the query. No code changes are
 * needed when options are added, renamed, or reordered — the engine just
 * finds the best real collection(s) for whatever text is there today.
 */
(function (global) {
  'use strict';

  var STOPWORDS = {
    the: 1, and: 1, for: 1, with: 1, from: 1, this: 1, that: 1, a: 1, an: 1,
    of: 1, in: 1, on: 1, to: 1, or: 1, is: 1, are: 1, by: 1, at: 1, ea: 1,
    each: 1, pk: 1, pack: 1, cs: 1, case: 1, your: 1, you: 1
  };

  function tokenize(str) {
    if (!str) return [];
    return String(str)
      .toLowerCase()
      .replace(/[™®©]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .map(function (w) { return w.trim(); })
      .filter(function (w) {
        return w.length > 2 && !STOPWORDS[w] && !/^\d+$/.test(w);
      });
  }

  // Lightweight fuzzy match for word-form variants the tokenizer can't tell
  // apart without a full stemmer (e.g. "filtration" vs "filter", "carboys"
  // vs "carboy" already works via exact match, but "filters" vs "filtration"
  // doesn't share a token). A shared prefix of 5+ chars between two tokens
  // of reasonable length is treated as a partial match at reduced weight —
  // cheap, dependency-free, and good enough at this vocabulary size (~800
  // tokens) to meaningfully boost recall without many false positives.
  function sharesPrefix(a, b) {
    if (a === b) return true;
    var minLen = Math.min(a.length, b.length);
    if (minLen < 5) return false;
    var prefixLen = Math.min(5, minLen);
    return a.slice(0, prefixLen) === b.slice(0, prefixLen) && Math.abs(a.length - b.length) <= 4;
  }

  function bestTokenMatch(queryTok, list) {
    // Returns 1 for exact match, 0.55 for fuzzy prefix match, 0 for none.
    for (var i = 0; i < list.length; i++) {
      if (list[i] === queryTok) return 1;
    }
    for (i = 0; i < list.length; i++) {
      if (sharesPrefix(queryTok, list[i])) return 0.55;
    }
    return 0;
  }

  function uniq(arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      if (!seen[arr[i]]) { seen[arr[i]] = 1; out.push(arr[i]); }
    }
    return out;
  }

  /**
   * PFEngine(indexPayload)
   * indexPayload = the parsed contents of pf-category-index.json
   *   { generatedAt, sourceGeneratedAt, count, idf: {tok: weight}, nodes: [...] }
   */
  function PFEngine(indexPayload) {
    this.nodes = (indexPayload && indexPayload.nodes) || [];
    this.idf = (indexPayload && indexPayload.idf) || {};
    this.byHandle = {};
    for (var i = 0; i < this.nodes.length; i++) {
      var n = this.nodes[i];
      if (!this.byHandle[n.h]) this.byHandle[n.h] = n; // first occurrence wins
    }
  }

  PFEngine.prototype._idfOf = function (tok) {
    var w = this.idf[tok];
    return typeof w === 'number' ? w : 0.3;
  };

  PFEngine.prototype._scoreNode = function (node, queryTokens, titleTokens) {
    var score = 0;
    var i, tok, w;
    var tkw = node.tkw || [];
    var pkw = node.pkw || [];

    for (i = 0; i < queryTokens.length; i++) {
      tok = queryTokens[i];
      w = this._idfOf(tok);
      var tMatch = bestTokenMatch(tok, tkw);
      var pMatch = bestTokenMatch(tok, pkw);
      if (tMatch > 0) score += 3 * w * tMatch;
      else if (pMatch > 0) score += 1 * w * pMatch;
    }

    if (titleTokens && titleTokens.length) {
      var ownTitle = tokenize(node.t);
      var hits = 0;
      for (i = 0; i < titleTokens.length; i++) {
        if (ownTitle.indexOf(titleTokens[i]) !== -1) hits++;
      }
      if (hits > 0) {
        score += hits * 4;
        if (hits === titleTokens.length) score += 3;
      }
    }

    score += Math.log(1 + (node.tpc || 0)) * 0.08;
    return score;
  };

  /**
   * search(queryText, opts)
   *   queryText:  plain string (concatenated label + sub_label etc.)
   *   opts.titleText: the "primary" text (e.g. just the Step 1 label) that
   *                   gets the exact-title-match bonus treatment.
   *   opts.topN:  max results to return (default 8, pre-dedup)
   *   opts.minLevel / opts.maxLevel: restrict which tree depths are eligible
   *   opts.excludeHandles: array of handles to skip entirely
   * Returns: array of { node, score }, highest first, deduped so no result
   * is an ancestor of another result already present (keeps only the most
   * specific node per branch), and no handle repeats.
   */
  PFEngine.prototype.search = function (queryText, opts) {
    opts = opts || {};
    var queryTokens = tokenize(queryText);
    var titleTokens = tokenize(opts.titleText || '');
    var minLevel = opts.minLevel || 1;
    var maxLevel = opts.maxLevel || 6;
    var excludeHandles = {};
    (opts.excludeHandles || []).forEach(function (h) { excludeHandles[h] = 1; });

    if (!queryTokens.length) return [];

    var scored = [];
    for (var i = 0; i < this.nodes.length; i++) {
      var node = this.nodes[i];
      if (node.lvl < minLevel || node.lvl > maxLevel) continue;
      if (excludeHandles[node.h]) continue;
      var s = this._scoreNode(node, queryTokens, titleTokens);
      if (s > 0) scored.push({ node: node, score: s });
    }

    scored.sort(function (a, b) { return b.score - a.score; });

    // Dedup: keep only the highest-scoring node per unique handle, and drop
    // any result that is a direct ancestor of an already-kept higher result
    // (prevents "Laboratory Safety" + "Laboratory Safety > Chemical Storage"
    // both showing up when the child is clearly the better, more specific
    // match for the given query).
    var kept = [];
    var keptHandles = {};
    for (i = 0; i < scored.length; i++) {
      var entry = scored[i];
      var h = entry.node.h;
      if (keptHandles[h]) continue;
      var isAncestorOfKept = false;
      for (var k = 0; k < kept.length; k++) {
        var keptAnc = kept[k].node.anc || [];
        if (keptAnc.indexOf(h) !== -1) { isAncestorOfKept = true; break; }
      }
      if (isAncestorOfKept) continue;
      kept.push(entry);
      keptHandles[h] = 1;
      if (kept.length >= (opts.topN || 8)) break;
    }

    return kept;
  };

  /**
   * bestMatch(queryText, opts) → single top result or null.
   */
  PFEngine.prototype.bestMatch = function (queryText, opts) {
    var results = this.search(queryText, Object.assign({}, opts, { topN: 1 }));
    return results.length ? results[0] : null;
  };

  /**
   * topMatchWithAlternates(queryText, opts)
   * Returns { primary, alternates } where alternates are additional distinct
   * results whose score is within `opts.closeRatio` (default 0.72) of the
   * primary's score — i.e. "close enough to also be worth showing" — capped
   * at opts.maxAlternates (default 2).
   */
  PFEngine.prototype.topMatchWithAlternates = function (queryText, opts) {
    opts = opts || {};
    var closeRatio = typeof opts.closeRatio === 'number' ? opts.closeRatio : 0.72;
    var maxAlternates = typeof opts.maxAlternates === 'number' ? opts.maxAlternates : 2;
    var results = this.search(queryText, Object.assign({}, opts, { topN: 6 }));
    if (!results.length) return { primary: null, alternates: [] };
    var primary = results[0];
    var alternates = [];
    for (var i = 1; i < results.length && alternates.length < maxAlternates; i++) {
      if (results[i].score >= primary.score * closeRatio) alternates.push(results[i]);
    }
    return { primary: primary, alternates: alternates };
  };

  PFEngine.prototype.getByHandle = function (handle) {
    return this.byHandle[handle] || null;
  };

  PFEngine.prototype.children = function (handle) {
    var out = [];
    for (var i = 0; i < this.nodes.length; i++) {
      if (this.nodes[i].parent === handle) out.push(this.nodes[i]);
    }
    return out;
  };

  /**
   * descendants(handle) → flat array of every node below `handle` (any depth).
   */
  PFEngine.prototype.descendants = function (handle) {
    var out = [];
    for (var i = 0; i < this.nodes.length; i++) {
      var anc = this.nodes[i].anc || [];
      if (anc.indexOf(handle) !== -1) out.push(this.nodes[i]);
    }
    return out;
  };

  /**
   * searchWithin(rootHandle, queryText, opts)
   * Same scoring as search(), but restricted to the subtree under
   * `rootHandle` (inclusive). This is the key building block for Step 2/3
   * refinement: once Step 1 has already identified a primary category
   * (e.g. "Laboratory Safety"), Step 2 ("Research Lab" / "Industrial" /
   * etc.) and Step 3 (quantity) selections are used to find the single
   * best, most specific collection *inside that category* rather than
   * re-searching the whole 523-node tree and risking a jump to a
   * completely unrelated branch.
   */
  PFEngine.prototype.searchWithin = function (rootHandle, queryText, opts) {
    opts = opts || {};
    var pool = rootHandle ? this.descendants(rootHandle).concat([this.getByHandle(rootHandle)].filter(Boolean)) : this.nodes;
    var queryTokens = tokenize(queryText);
    var titleTokens = tokenize(opts.titleText || '');
    if (!queryTokens.length) return [];

    var self = this;
    var scored = [];
    pool.forEach(function (node) {
      if (!node) return;
      var s = self._scoreNode(node, queryTokens, titleTokens);
      if (s > 0) scored.push({ node: node, score: s });
    });
    scored.sort(function (a, b) { return b.score - a.score; });

    var kept = [];
    var keptHandles = {};
    for (var i = 0; i < scored.length; i++) {
      var entry = scored[i];
      if (keptHandles[entry.node.h]) continue;
      var isAncestorOfKept = false;
      for (var k = 0; k < kept.length; k++) {
        if ((kept[k].node.anc || []).indexOf(entry.node.h) !== -1) { isAncestorOfKept = true; break; }
      }
      if (isAncestorOfKept) continue;
      kept.push(entry);
      keptHandles[entry.node.h] = 1;
      if (kept.length >= (opts.topN || 6)) break;
    }
    return kept;
  };

  PFEngine.tokenize = tokenize;
  PFEngine.uniq = uniq;

  global.PFEngine = PFEngine;
})(typeof window !== 'undefined' ? window : this);
